import { PathsV1VehicleValuationUuidGetParametersQueryExtend } from "@/api-schema-enums";
import { Can } from "@/casl-provider";
import { useVehicleValuationByUuidQuery } from "@/common/api/vehicle-valuation/use-vehicle-valuation-by-uuid.query";
import { BrandLogo, BrandLogoVariant } from "@/components/brand-logo/brand-logo";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { UuidExistenceWrapper } from "@/components/uuid-existence-wrapper/uuid-existence-wrapper";
import { useTranslation } from "react-i18next";
import { useParams } from "wouter";
import { VehicleValuationConfirmFormFields } from "./_components/vehicle-valuation-confirm-form-fields";

const VEHICLE_VALUATION_CONFIRM_EXTEND: PathsV1VehicleValuationUuidGetParametersQueryExtend[] = [
  PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle,
  PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle_ModelGeneration,
  PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle_ModelGeneration_ModelFamily,
  PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle_ModelGeneration_ModelFamily_Brand,
  PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle_OwningBranch,
  PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle_CurrentBranch,
];

interface ConfirmViewParams {
  uuid: string;
}

export function ConfirmView() {
  return (
    <Can I="manage" a="VehicleValuation">
      <ConfirmContent />
    </Can>
  );
}

function ConfirmContent() {
  const { t } = useTranslation(["common", "masterdata"]);
  const params = useParams<ConfirmViewParams>();
  const uuid = params.uuid;

  const vehicleValuationQuery = useVehicleValuationByUuidQuery(uuid, {
    parameters: {
      extend: VEHICLE_VALUATION_CONFIRM_EXTEND,
    },
  });

  const valuation = vehicleValuationQuery.data;
  const brand = valuation?.vehicle?.modelGeneration?.modelFamily?.brand;

  const vehicleName = valuation?.vehicle
    ? [brand?.name, valuation.vehicle.modelGeneration?.modelFamily?.name, valuation.vehicle.modelGeneration?.name]
        .filter(Boolean)
        .join(" ") || t("masterdata:ConfirmVehicleValuation")
    : t("masterdata:ConfirmVehicleValuation");

  const title = brand ? (
    <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight sm:text-3xl">
      <BrandLogo variant={BrandLogoVariant.MEDIUM} brand={brand} />
      <span>{vehicleName}</span>
    </h1>
  ) : (
    vehicleName
  );

  return (
    <UuidExistenceWrapper
      uuid={uuid}
      query={useVehicleValuationByUuidQuery}
      queryResult={vehicleValuationQuery}
      notFoundProps={{
        title: t("masterdata:VehicleValuationNotFound"),
        message: t("masterdata:RequestedVehicleValuationDoesNotExist"),
        description: t("masterdata:PleaseCheckVehicleValuationOrContactSupport"),
        backTo: "~/fab/list",
        backToLabel: t("masterdata:BackToVehicleValuations"),
      }}
    >
      {valuation ? (
        <CenteredLayout
          variant={CenteredLayoutVariant.Form}
          title={title}
          subTitle={t("masterdata:ConfirmVehicleValuationDescription")}
        >
          <VehicleValuationConfirmFormFields valuation={valuation} />
        </CenteredLayout>
      ) : null}
    </UuidExistenceWrapper>
  );
}
